import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Plus, Truck, Package, Barcode, Warning, Check, ClipboardText, FileText,
  Eye, X, CaretRight, ListChecks, Lock, Prohibit, Pause, Trash, DownloadSimple,
  Camera, ImageSquare, Printer, Lightning, Gauge, CalendarCheck, Stack, ArrowUUpLeft,
} from '@phosphor-icons/react';
import { getDispatchLoadSequence, dispatchManifestPdfUrl, dispatchBolPdfUrl, markDispatchDelivered, markDispatchReturned } from '../../../lib/api';
import { SumItem, SummaryBar, STATUS_META, NEXT_STATUS } from '../_shared';
import { useAuth } from '../../../context/AuthContext';

export const LoadingTab = ({ detail, isManager, onOpenScan, onOpenSeal, onOpenPhoto, onOpenGallery, onOpenPalletLog }) => {
  const canScan = ['admin', 'manager', 'packer'].includes(useAuth().user?.role) && detail.status === 'loading';
  const canSeal = isManager && (detail.status === 'loading' || detail.status === 'loaded');
  const canPhoto = ['admin', 'manager', 'packer'].includes(useAuth().user?.role) && detail.status !== 'cancelled';
  // Pallet logging stays available across the loading + dispatched window so
  // operators can backfill if they forget at the moment of seal-up.
  const canLogPallets = ['admin', 'manager', 'packer'].includes(useAuth().user?.role)
    && ['loading', 'loaded', 'dispatched'].includes(detail.status);
  const scans = detail.loaded_scans || [];
  const photoCount = (detail.photos || []).length;
  return (
    <div className="mt-4 space-y-3" data-testid="loading-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">Loading Events</h3>
        <div className="flex gap-2 flex-wrap">
          {canScan && (
            <Button size="sm" onClick={onOpenScan} data-testid="open-scan-btn" className="bg-amber-600 hover:bg-amber-700 text-white rounded-none">
              <Barcode size={14} weight="bold" className="mr-1" /> Scan to load
            </Button>
          )}
          {canPhoto && (
            <Button size="sm" variant="outline" onClick={onOpenPhoto} data-testid="open-photo-btn" className="rounded-none">
              <Camera size={14} weight="bold" className="mr-1" /> Capture photo
            </Button>
          )}
          {canLogPallets && (
            <Button size="sm" variant="outline" onClick={onOpenPalletLog} data-testid="open-pallet-log-btn" className="rounded-none">
              <Stack size={14} weight="bold" className="mr-1" /> Log pallets
            </Button>
          )}
          {photoCount > 0 && (
            <Button size="sm" variant="outline" onClick={onOpenGallery} data-testid="open-gallery-btn" className="rounded-none">
              <ImageSquare size={14} weight="bold" className="mr-1" /> Gallery ({photoCount})
            </Button>
          )}
          {canSeal && !detail.seal_numbers?.length && (
            <Button size="sm" onClick={onOpenSeal} data-testid="open-seal-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none">
              <Lock size={14} weight="bold" className="mr-1" /> Apply seal & checklist
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => window.open(dispatchManifestPdfUrl(detail.id), '_blank')} data-testid="open-manifest-btn" className="rounded-none">
            <Printer size={14} weight="bold" className="mr-1" /> Manifest PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.open(dispatchBolPdfUrl(detail.id), '_blank')} data-testid="open-bol-btn" className="rounded-none border-brand text-brand">
            <FileText size={14} weight="bold" className="mr-1" /> BOL PDF
          </Button>
          {detail.status === 'loading' && (
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                const { data } = await getDispatchLoadSequence(detail.id);
                alert(`Load sequence hint:\n\n${(data.sequence || []).slice(0, 10).map(s => `${s.position}/${data.total_items}  ${s.sku_code} · ${s.customer_name} · ${s.qty}u`).join('\n')}\n\n${data.hint}`);
              } catch (e) { toast.error(e?.response?.data?.detail || 'Load sequence failed'); }
            }} data-testid="load-sequence-btn" className="rounded-none">
              <ListChecks size={14} weight="bold" className="mr-1" /> Load sequence (LIFO)
            </Button>
          )}
          {detail.status === 'dispatched' && !detail.delivered_at && isManager && (
            <Button size="sm" onClick={async () => {
              if (!window.confirm('Mark as delivered now?')) return;
              try { await markDispatchDelivered(detail.id, { free_time_hours: 48 }); toast.success('Delivered'); window.location.reload(); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Mark delivered failed'); }
            }} data-testid="mark-delivered-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-none">
              <Check size={14} weight="bold" className="mr-1" /> Mark delivered
            </Button>
          )}
          {detail.delivered_at && !detail.returned_at && isManager && (
            <Button size="sm" onClick={async () => {
              if (!window.confirm('Mark container returned now?')) return;
              try { await markDispatchReturned(detail.id, {}); toast.success('Returned'); window.location.reload(); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Mark returned failed'); }
            }} data-testid="mark-returned-btn" className="bg-teal-600 hover:bg-teal-700 text-white rounded-none">
              <ArrowUUpLeft size={14} weight="bold" className="mr-1" /> Mark returned
            </Button>
          )}
        </div>
      </div>
      {detail.seal_numbers?.length > 0 && (
        <div className="border border-emerald-300 bg-emerald-50 p-3" data-testid="seal-info">
          <p className="text-xs uppercase tracking-widest font-bold text-emerald-800 mb-1">Sealed</p>
          <div className="flex flex-wrap gap-2">
            {detail.seal_numbers.map(s => (
              <span key={s.seal_id} className="font-mono text-sm bg-card border border-emerald-300 px-2 py-0.5">{s.seal_number}</span>
            ))}
          </div>
          {detail.checklist && (
            <p className="text-xs text-emerald-700 mt-2">
              Checklist signed by {detail.checklist.signed_name} · {new Date(detail.checklist.signed_at).toLocaleString()}
              {detail.checklist.temperature_c != null && ` · ${detail.checklist.temperature_c}°C`}
              {detail.checklist.dg_verified && ' · DG verified'}
            </p>
          )}
        </div>
      )}
      <div className="border border-border max-h-80 overflow-y-auto">
        <table className="w-full table-dense">
          <thead>
            <tr>
              <th>SKU</th>
              <th className="text-right">Qty</th>
              <th>Carton</th>
              <th>Position</th>
              <th>Scanned</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {scans.map(s => (
              <tr key={s.scan_id} className={s.forced ? 'bg-amber-50' : ''} data-testid={`scan-${s.scan_id}`}>
                <td className="font-mono font-bold">{s.sku_code || <span className="text-muted-foreground">—</span>}</td>
                <td className="text-right font-mono">{s.qty}</td>
                <td className="font-mono text-xs">{s.carton_id || '—'}</td>
                <td className="font-mono text-xs">{s.position || '—'}</td>
                <td className="text-xs font-mono text-muted-foreground">{new Date(s.scanned_at).toLocaleTimeString()}</td>
                <td className="text-xs text-muted-foreground">{s.forced ? <span className="text-amber-700 font-bold">forced</span> : 'scan'}</td>
              </tr>
            ))}
            {scans.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No scans yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

